import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemDetail } from '@up2d8/api-client';
import {
  BRAND_DEFAULTS,
  type BrandStyleSettings,
  type TenantTheme,
  type UpdateTenantThemeRequest,
} from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { WebsiteFeedCard } from '../communication/website-feed-card';
import { applyTenantTheme } from '../../shared/theme';
import {
  AdminIcon,
  AdminPage,
  Btn,
  Card,
  CardHead,
  CardPad,
  Field,
  Input,
  Note,
  PageHead,
  Row,
  useAdminToast,
} from '../admin-kit';
import {
  ColoursCard,
  EmailCard,
  LoginCard,
  RESET_ALL,
  ResetAllModal,
  TypeCard,
  draftFrom,
  draftRequest,
  previewOf,
  up2d8Defaults,
  type BrandDraft,
} from './brand-form';
import {
  BACKGROUND_ACCEPT,
  BITMAP_ACCEPT,
  BrandImageField,
  LOGO_ACCEPT,
} from './brand-image-field';
import { BrandPreview, initialen } from './brand-preview';
import './huisstijl.css';

/** Wat een bedrijf koos als de server (vóór MRK-1) nog geen `own` meestuurt: niets. */
const NIETS_GEKOZEN: BrandStyleSettings = {
  primary: null,
  accent: null,
  sidebar: null,
  fontHeading: null,
  fontBody: null,
  radius: null,
  loginTitle: null,
  loginText: null,
  emailSenderName: null,
};

/** De keuzes en het geërfde uit het thema, met een terugval voor een oudere server. */
function levels(theme: TenantTheme): { own: BrandStyleSettings; inherited: BrandStyleSettings } {
  return {
    own: theme.own ?? NIETS_GEKOZEN,
    inherited: theme.inherited ?? up2d8Defaults(theme.productName ?? BRAND_DEFAULTS.productName),
  };
}

/**
 * De huisstijl van een klant instellen (Stap 43, Extra 1, I1 van BHR-8; uitgebreid in MRK-3).
 *
 * Bovenaan de organisatie: de naam die de medewerker ziet, de twee logo's en het pictogram. Dan de
 * kaarten van het merk: kleuren (met de optionele zijbalk), lettertype en vorm, het aanmeldscherm
 * en de e-mail. Elk veld dat leeg blijft, erft van het platformmerk (ADR-0084), en de hint eronder
 * zegt wat dat dan is.
 *
 * Rechts het voorbeeld, live: het toont wat de app ervan maakt, ook vóór het opslaan.
 */
export function BrandingPage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const toast = useAdminToast();
  const queryClient = useQueryClient();

  const theme = useQuery({
    queryKey: ['tenant-theme'],
    queryFn: async (): Promise<TenantTheme> => {
      const { data, error } = await api.GET('/api/v1/tenant/theme');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as TenantTheme;
    },
  });

  const [draft, setDraft] = useState<BrandDraft | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  // De velden vullen met wat het bedrijf zelf koos (`own`), niet met wat er geldt: wie niets koos,
  // hoort een leeg veld te zien met het geërfde als plaatshouder.
  // Op de keuzes zelf en niet op het hele thema: een nieuw logo geeft een nieuw thema terug (met
  // nieuwe ondertekende URL's), en dat mag niet wissen wat er nog niet opgeslagen is.
  const levelsKey = theme.data ? JSON.stringify(levels(theme.data)) : null;
  useEffect(() => {
    if (!levelsKey) return;
    const { own, inherited } = JSON.parse(levelsKey) as ReturnType<typeof levels>;
    setDraft(draftFrom(own, inherited));
  }, [levelsKey]);

  const toepassen = (next: TenantTheme): void => {
    // Meteen toepassen: de beheerder hoort het resultaat te zien zonder te herladen.
    applyTenantTheme(next);
    queryClient.setQueryData(['tenant-theme'], next);
    void queryClient.invalidateQueries({ queryKey: ['tenant-theme'] });
  };

  const save = useMutation({
    mutationFn: async (input: UpdateTenantThemeRequest) => {
      const { data, error } = await api.PUT('/api/v1/tenant/theme', { body: input });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      return data as TenantTheme;
    },
    onMutate: () => setFout(null),
    onSuccess: (next) => {
      toepassen(next);
      toast.show(t('branding.saved'));
    },
    // De server legt uit wát er mis is met de kleur; dat is nuttiger dan een algemene melding.
    onError: (error) => setFout(problemDetail(error) ?? t('branding.failed')),
  });

  /*
    Alles terug naar standaard: elk veld op `null`, en de eigen achtergrond van het aanmeldscherm
    weg. De logo's en het icoon alleen als dat aangevinkt is (zie `ResetAllModal`). De weergavenaam
    blijft: die is geen huisstijl maar hoe het bedrijf heet.
  */
  const resetAll = useMutation({
    mutationFn: async (withLogos: boolean) => {
      const reset = await api.PUT('/api/v1/tenant/theme', { body: RESET_ALL });
      if (reset.error || !reset.data) throw reset.error ?? new Error('Opslaan mislukt');
      let latest = reset.data as TenantTheme;
      const leeg = { body: { fileId: null } };
      if (latest.ownLoginBackground) {
        const { data, error } = await api.PUT('/api/v1/tenant/login-background', leeg);
        if (error || !data) throw error ?? new Error('Opslaan mislukt');
        latest = data as TenantTheme;
      }
      if (withLogos) {
        for (const [set, path] of [
          [latest.logoUrl, '/api/v1/tenant/logo'],
          [latest.logoDarkUrl, '/api/v1/tenant/logo-dark'],
          [latest.appIconUrls, '/api/v1/tenant/app-icon'],
        ] as const) {
          if (!set) continue;
          const { data, error } = await api.PUT(path, leeg);
          if (error || !data) throw error ?? new Error('Opslaan mislukt');
          latest = data as TenantTheme;
        }
      }
      return latest;
    },
    onSuccess: (next) => {
      setResetOpen(false);
      toepassen(next);
      toast.show(t('beheer.merk.resetAllDone'));
    },
  });

  if (!theme.data || !draft) {
    return (
      <AdminPage>
        <PageHead title={t('branding.title')} sub={t('beheer.huisstijl.sub')} />
        {theme.isError ? (
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        ) : (
          <LoadingState rows={5} label={t('common.loading')} />
        )}
      </AdminPage>
    );
  }

  const current = theme.data;
  const { own, inherited } = levels(current);
  const productName = current.productName ?? BRAND_DEFAULTS.productName;
  const brand = previewOf(draft, inherited);
  const vernieuw = async (next: TenantTheme): Promise<void> => {
    toepassen(next);
  };
  const tile = (
    <span className="a-orgtile" style={{ background: current.primary, color: current.onPrimary }}>
      {initialen(current.displayName)}
    </span>
  );
  /** Een afbeelding aan zijn plek hangen; elke route geeft het nieuwe thema terug. */
  const attach =
    (
      path:
        | '/api/v1/tenant/logo'
        | '/api/v1/tenant/logo-dark'
        | '/api/v1/tenant/app-icon'
        | '/api/v1/tenant/login-background',
    ) =>
    async (fileId: string | null): Promise<void> => {
      const { data, error } = await api.PUT(path, { body: { fileId } });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      await vernieuw(data as TenantTheme);
    };

  return (
    <AdminPage>
      <PageHead
        title={t('branding.title')}
        sub={t('beheer.huisstijl.sub')}
        actions={
          <>
            <Btn variant="ghost" testId="reset-all" onClick={() => setResetOpen(true)}>
              {t('beheer.merk.resetAll')}
            </Btn>
            <Btn
              icon={<AdminIcon name="check" size={16} />}
              disabled={save.isPending}
              testId="save-branding"
              onClick={() => save.mutate(draftRequest(draft))}
            >
              {t('branding.save')}
            </Btn>
          </>
        }
      />

      <div className="a-grid a-brand-grid">
        <div className="a-stack">
          <OrganisationCard theme={current} onSaved={() => toast.show(t('branding.nameSaved'))} />
          {/* De openbare websitefeed (WEB-1): naam en logo van hierboven staan ook bovenaan de feed. */}
          {has('communication.website_feed.manage') ? (
            <WebsiteFeedCard onSaved={(message) => toast.show(message)} />
          ) : null}

          <Card>
            <CardHead title={t('beheer.huisstijl.logos')} />
            <CardPad>
              <div className="a-grid three">
                <BrandImageField
                  kind="logo"
                  label={t('beheer.huisstijl.logoLight')}
                  hint={t('beheer.merk.logoLightHint')}
                  url={current.logoUrl}
                  fallback={tile}
                  accept={LOGO_ACCEPT}
                  purpose="tenant_logo"
                  attach={attach('/api/v1/tenant/logo')}
                  onSaved={toast.show}
                />
                <BrandImageField
                  kind="logo-dark"
                  label={t('beheer.huisstijl.logoDark')}
                  hint={t('beheer.merk.logoDarkHint')}
                  url={current.logoDarkUrl}
                  fallback={tile}
                  dark
                  accept={LOGO_ACCEPT}
                  purpose="tenant_logo"
                  attach={attach('/api/v1/tenant/logo-dark')}
                  onSaved={toast.show}
                />
                <BrandImageField
                  kind="app-icon"
                  label={t('beheer.huisstijl.appIcon')}
                  hint={t('beheer.huisstijl.appIconHint')}
                  url={current.appIconUrls?.size192 ?? null}
                  fallback={tile}
                  accept={BITMAP_ACCEPT}
                  purpose="tenant_logo"
                  attach={attach('/api/v1/tenant/app-icon')}
                  onSaved={toast.show}
                />
              </div>
            </CardPad>
          </Card>

          <ColoursCard
            level="tenant"
            draft={draft}
            onChange={setDraft}
            own={own}
            inherited={inherited}
            error={fout}
            reset={
              <Btn
                variant="ghost"
                size="sm"
                disabled={save.isPending}
                testId="reset-branding"
                onClick={() => save.mutate({ primary: null, accent: null })}
              >
                {t('beheer.merk.resetTo', { name: productName })}
              </Btn>
            }
          />
          <TypeCard
            level="tenant"
            draft={draft}
            onChange={setDraft}
            own={own}
            inherited={inherited}
          />
          <LoginCard
            level="tenant"
            draft={draft}
            onChange={setDraft}
            own={own}
            inherited={inherited}
            background={
              <BrandImageField
                kind="login-background"
                label={t('beheer.merk.loginBackground')}
                hint={
                  current.loginBackgroundUrl && !current.ownLoginBackground
                    ? `${t('beheer.merk.loginBackgroundInherited')} · ${t('beheer.merk.loginBackgroundHint')}`
                    : t('beheer.merk.loginBackgroundHint')
                }
                url={current.loginBackgroundUrl ?? null}
                hasImage={Boolean(current.ownLoginBackground)}
                fallback={<span className="a-hint">{t('beheer.merk.noImage')}</span>}
                dark
                accept={BACKGROUND_ACCEPT}
                purpose="login_background"
                attach={attach('/api/v1/tenant/login-background')}
                onSaved={toast.show}
              />
            }
          />
          <EmailCard
            level="tenant"
            draft={draft}
            onChange={setDraft}
            own={own}
            inherited={inherited}
          />
        </div>

        <div className="a-stack a-brand-side">
          <BrandPreview
            brand={brand}
            name={current.displayName}
            logoUrl={current.logoUrl}
            logoDarkUrl={current.logoDarkUrl}
            appIconUrl={current.appIconUrls?.size192 ?? null}
            loginBackgroundUrl={current.loginBackgroundUrl ?? null}
            showLogin
          />
        </div>
      </div>

      <ResetAllModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        title={t('beheer.merk.resetAllTitle')}
        text={t('beheer.merk.resetAllText')}
        logosLabel={t('beheer.merk.resetAllLogos')}
        busy={resetAll.isPending}
        error={resetAll.isError ? (problemDetail(resetAll.error) ?? t('branding.failed')) : null}
        onConfirm={(withLogos) => resetAll.mutate(withLogos)}
      />
      {toast.element}
    </AdminPage>
  );
}

/**
 * De organisatie: weergavenaam en officiële naam (Extra 1).
 *
 * Wat UP2D8 beheert, staat er alleen-lezen bij. Weglaten zou de vraag oproepen waar de officiële
 * naam dan wél te wijzigen is; de uitleg staat eronder.
 */
function OrganisationCard({ theme, onSaved }: { theme: TenantTheme; onSaved: () => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [naam, setNaam] = useState('');

  // Niet de weergavenaam maar de gekozen naam: wie niets koos, hoort een leeg veld te zien met de
  // officiële naam als placeholder, en niet een veld dat lijkt alsof hij al iets invulde.
  useEffect(() => {
    setNaam(theme.displayName === theme.legalName ? '' : theme.displayName);
  }, [theme.displayName, theme.legalName]);

  const saveName = useMutation({
    mutationFn: async () => {
      // Alleen de naam: sinds MRK-1 is elk veld optioneel, en weggelaten = ongewijzigd. Vroeger
      // gingen de kleuren mee op basis van `custom`, maar dat is sindsdien ook waar als alleen het
      // platform andere kleuren heeft - en dan zou de naam opslaan de kleuren vastzetten.
      const { data, error } = await api.PUT('/api/v1/tenant/theme', {
        body: { displayName: naam.trim() || null },
      });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      return data as TenantTheme;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenant-theme'] });
      onSaved();
    },
  });

  return (
    <Card testId="organisation-card">
      <CardHead title={t('branding.organisation')} />
      <CardPad>
        <Row>
          <Field
            label={t('branding.displayName')}
            hint={t('beheer.huisstijl.displayNameHint')}
            error={
              saveName.isError ? (problemDetail(saveName.error) ?? t('branding.failed')) : undefined
            }
          >
            {(props) => (
              <form
                className="a-inline"
                onSubmit={(event) => {
                  event.preventDefault();
                  saveName.mutate();
                }}
              >
                <Input
                  {...props}
                  value={naam}
                  placeholder={theme.legalName}
                  maxLength={30}
                  data-testid="display-name"
                  onChange={(event) => setNaam(event.target.value)}
                />
                <Btn
                  type="submit"
                  variant="sec"
                  size="sm"
                  disabled={saveName.isPending}
                  testId="save-display-name"
                >
                  {t('branding.saveName')}
                </Btn>
              </form>
            )}
          </Field>
          <Field label={t('branding.legalName')} hint={t('beheer.huisstijl.legalNameHint')}>
            {(props) => (
              <Input {...props} value={theme.legalName} readOnly data-testid="organisation-facts" />
            )}
          </Field>
        </Row>
      </CardPad>
    </Card>
  );
}
